/**
 * Portfolio On-Chain Page
 *
 * Main page for viewing on-chain portfolio data fetched from DeBank.
 * Displays wallet balances, token holdings, DeFi positions, and transaction history.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useOnChainPortfolio } from '../hooks/useOnChainPortfolio';
import { useToast } from '../components/Toast';
import { useCurrencyContext } from '../contexts/CurrencyContext';
import { fmt } from '../utils/formatters';
import type { UserMode } from '../hooks/useUserMode';
import type { OnChainToken, OnChainDefiPosition, OnChainTransaction, OnChainNFT, OnChainApproval, OnChainHistoryTx, TransactionType } from '../types/onchain';
import './PortfolioOnChain.css';

// =====================================================
// TYPES
// =====================================================

interface PortfolioOnChainProps {
  userMode?: UserMode;
}

type TabType = 'overview' | 'tokens' | 'defi' | 'nfts' | 'approvals' | 'history' | 'transactions';

// Risk level colors for approvals
const RISK_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  low: { bg: 'rgba(16, 185, 129, 0.15)', text: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
  medium: { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' },
  high: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' },
  critical: { bg: 'rgba(220, 38, 38, 0.2)', text: '#dc2626', border: 'rgba(220, 38, 38, 0.4)' },
};

// History category labels
const HISTORY_CATE_LABELS: Record<string, { label: string; color: string }> = {
  receive: { label: 'Reçu', color: '#10b981' },
  send: { label: 'Envoyé', color: '#ef4444' },
  swap: { label: 'Swap', color: '#8b5cf6' },
  approve: { label: 'Approval', color: '#f59e0b' },
  stake: { label: 'Stake', color: '#06b6d4' },
  unstake: { label: 'Unstake', color: '#06b6d4' },
  deposit: { label: 'Dépôt', color: '#3b82f6' },
  withdraw: { label: 'Retrait', color: '#3b82f6' },
  mint: { label: 'Mint', color: '#ec4899' },
  burn: { label: 'Burn', color: '#f97316' },
  claim: { label: 'Claim', color: '#22c55e' },
};

// =====================================================
// CHAIN CONFIG
// =====================================================

const CHAIN_CONFIG: Record<string, { name: string; color: string; icon: string }> = {
  eth: { name: 'ETH', color: '#627EEA', icon: '⟠' },
  bsc: { name: 'BSC', color: '#F3BA2F', icon: '⬡' },
  polygon: { name: 'MATIC', color: '#8247E5', icon: '⬡' },
  arb: { name: 'ARB', color: '#28A0F0', icon: '⬡' },
  op: { name: 'OP', color: '#FF0420', icon: '⬡' },
  avax: { name: 'AVAX', color: '#E84142', icon: '⬡' },
  ftm: { name: 'FTM', color: '#1969FF', icon: '⬡' },
  cro: { name: 'CRO', color: '#002D74', icon: '⬡' },
  base: { name: 'BASE', color: '#0052FF', icon: '⬡' },
};

const TX_TYPE_CONFIG: Record<TransactionType, { label: string; color: string; bgColor: string }> = {
  buy: { label: 'Buy', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  sell: { label: 'Sell', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)' },
  swap: { label: 'Swap', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  transfer_in: { label: 'Received', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  transfer_out: { label: 'Sent', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)' },
  approve: { label: 'Approve', color: '#888', bgColor: 'rgba(136, 136, 136, 0.15)' },
  stake: { label: 'Stake', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  unstake: { label: 'Unstake', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  claim: { label: 'Claim', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  mint: { label: 'Mint', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.15)' },
  burn: { label: 'Burn', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.15)' },
  unknown: { label: 'Unknown', color: '#888', bgColor: 'rgba(136, 136, 136, 0.15)' },
};

// =====================================================
// SUB-COMPONENTS
// =====================================================

function WalletCard({
  wallet,
  isSelected,
  onClick,
  onSync,
  onDelete,
  isSyncing,
}: {
  wallet: { id: string; wallet_address: string; label: string | null; total_value_usd: number; sync_status: string };
  isSelected: boolean;
  onClick: () => void;
  onSync: () => void;
  onDelete: () => void;
  isSyncing: boolean;
}) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  return (
    <div
      className={`onchain-page__wallet-card ${isSelected ? 'onchain-page__wallet-card--selected' : ''}`}
      onClick={onClick}
    >
      <div className="onchain-page__wallet-header">
        <div className="onchain-page__wallet-icon">
          <Icons.Wallet size={22} />
        </div>
        <div className="onchain-page__wallet-info">
          <p className="onchain-page__wallet-label">{wallet.label || 'Wallet'}</p>
          <p className="onchain-page__wallet-address">
            {wallet.wallet_address.slice(0, 6)}...{wallet.wallet_address.slice(-4)}
          </p>
        </div>
        <div className="onchain-page__wallet-value">
          <p className="onchain-page__wallet-amount">${fmt.currency(wallet.total_value_usd, 0)}</p>
          <div className="onchain-page__wallet-actions">
            <button
              onClick={(e) => { e.stopPropagation(); onSync(); }}
              disabled={isSyncing}
              className={`onchain-page__wallet-sync ${wallet.sync_status === 'error' ? 'onchain-page__wallet-sync--error' : ''}`}
              title="Synchroniser"
            >
              {isSyncing ? '...' : wallet.sync_status === 'error' ? '⚠️' : '↻'}
            </button>
            <button
              onClick={handleDelete}
              className="onchain-page__wallet-delete"
              title="Supprimer"
            >
              <Icons.Trash2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div className="onchain-page__wallet-confirm" onClick={(e) => e.stopPropagation()}>
          <p>Supprimer ce wallet ?</p>
          <div className="onchain-page__wallet-confirm-actions">
            <button onClick={cancelDelete} className="onchain-page__wallet-confirm-cancel">
              Annuler
            </button>
            <button onClick={confirmDelete} className="onchain-page__wallet-confirm-delete">
              Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TokenRow({ token }: { token: OnChainToken }) {
  const chain = CHAIN_CONFIG[token.chain] || { name: token.chain, color: '#888', icon: '⬡' };
  const change24h = token.price_24h_change || 0;

  return (
    <div className="onchain-page__token-row">
      <div className="onchain-page__token-logo">
        {token.logo_url ? (
          <img src={token.logo_url} alt={token.symbol} />
        ) : (
          <span className="onchain-page__token-logo-fallback">{token.symbol[0]}</span>
        )}
      </div>

      <div className="onchain-page__token-info">
        <div className="onchain-page__token-name-row">
          <span className="onchain-page__token-symbol">{token.symbol}</span>
          <span
            className="onchain-page__token-chain"
            style={{ backgroundColor: chain.color + '20', color: chain.color }}
          >
            {chain.name}
          </span>
        </div>
        <p className="onchain-page__token-balance">
          {fmt.number(token.balance, 4)} @ ${fmt.number(token.price_usd, 2)}
        </p>
      </div>

      <div className="onchain-page__token-value">
        <p className="onchain-page__token-usd">${fmt.currency(token.balance_usd, 2)}</p>
        <p className={`onchain-page__token-change ${change24h >= 0 ? 'onchain-page__token-change--positive' : 'onchain-page__token-change--negative'}`}>
          {change24h >= 0 ? '+' : ''}{fmt.number(change24h, 2)}%
        </p>
      </div>

      <div className="onchain-page__token-allocation">
        <p className="onchain-page__token-percent">{fmt.number(token.portfolio_percent, 1)}%</p>
        <div className="onchain-page__token-bar">
          <div
            className="onchain-page__token-bar-fill"
            style={{ width: `${Math.min(token.portfolio_percent, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function DefiPositionCard({ position }: { position: OnChainDefiPosition }) {
  const chain = CHAIN_CONFIG[position.chain] || { name: position.chain, color: '#888', icon: '⬡' };

  return (
    <div className="onchain-page__defi-card">
      <div className="onchain-page__defi-header">
        <div className="onchain-page__defi-logo">
          {position.protocol_logo ? (
            <img src={position.protocol_logo} alt={position.protocol_name || ''} />
          ) : (
            <span className="onchain-page__defi-logo-fallback">🏦</span>
          )}
        </div>

        <div className="onchain-page__defi-info">
          <div className="onchain-page__defi-name-row">
            <span className="onchain-page__defi-protocol">{position.protocol_name}</span>
            <span
              className="onchain-page__token-chain"
              style={{ backgroundColor: chain.color + '20', color: chain.color }}
            >
              {chain.name}
            </span>
          </div>
          <p className="onchain-page__defi-pool">{position.pool_name || position.position_type}</p>
        </div>

        <div className="onchain-page__defi-value">
          <p className="onchain-page__defi-amount">${fmt.currency(position.net_value_usd, 2)}</p>
          {position.apy && <p className="onchain-page__defi-apy">{fmt.number(position.apy, 2)}% APY</p>}
        </div>
      </div>

      <div className="onchain-page__defi-tokens">
        {position.tokens.map((token, i) => (
          <span key={i} className="onchain-page__defi-token">
            {fmt.number(token.amount, 4)} {token.symbol}
          </span>
        ))}
      </div>

      <div className="onchain-page__defi-stats">
        <div>
          <p className="onchain-page__defi-stat-label">Supplied</p>
          <p className="onchain-page__defi-stat-value">${fmt.currency(position.supplied_usd, 2)}</p>
        </div>
        <div>
          <p className="onchain-page__defi-stat-label">Borrowed</p>
          <p className="onchain-page__defi-stat-value">${fmt.currency(position.borrowed_usd, 2)}</p>
        </div>
        <div>
          <p className="onchain-page__defi-stat-label">Rewards</p>
          <p className="onchain-page__defi-stat-value onchain-page__defi-stat-value--green">
            ${fmt.currency(position.reward_usd, 2)}
          </p>
        </div>
      </div>
    </div>
  );
}

function TransactionRow({ tx }: { tx: OnChainTransaction }) {
  const typeConfig = TX_TYPE_CONFIG[tx.tx_type] || TX_TYPE_CONFIG.unknown;
  const chain = CHAIN_CONFIG[tx.chain] || { name: tx.chain, color: '#888', icon: '⬡' };

  return (
    <div className="onchain-page__tx-row">
      <div
        className="onchain-page__tx-icon"
        style={{ backgroundColor: typeConfig.bgColor }}
      >
        <span style={{ color: typeConfig.color }}>{chain.icon}</span>
      </div>

      <div className="onchain-page__tx-info">
        <div className="onchain-page__tx-type-row">
          <span className="onchain-page__tx-type" style={{ color: typeConfig.color }}>
            {typeConfig.label}
          </span>
          {tx.protocol_name && (
            <span className="onchain-page__tx-protocol">via {tx.protocol_name}</span>
          )}
        </div>
        <p className="onchain-page__tx-tokens">
          {tx.tx_type === 'swap' ? `${tx.swap_from_symbol} → ${tx.swap_to_symbol}` : tx.token_symbol || 'Unknown'}
        </p>
      </div>

      <div className="onchain-page__tx-amounts">
        {tx.tx_type === 'swap' ? (
          <>
            <p className="onchain-page__tx-amount onchain-page__tx-amount--out">
              -{fmt.number(tx.swap_from_amount || 0, 4)} {tx.swap_from_symbol}
            </p>
            <p className="onchain-page__tx-amount onchain-page__tx-amount--in">
              +{fmt.number(tx.swap_to_amount || 0, 4)} {tx.swap_to_symbol}
            </p>
          </>
        ) : (
          <p className="onchain-page__tx-amount">
            {tx.token_amount ? fmt.number(tx.token_amount, 4) : '-'} {tx.token_symbol}
          </p>
        )}
      </div>

      <div className="onchain-page__tx-meta">
        <p className="onchain-page__tx-date">{new Date(tx.block_timestamp).toLocaleDateString()}</p>
        <a
          href={`https://etherscan.io/tx/${tx.tx_hash}`}
          target="_blank"
          rel="noopener noreferrer"
          className="onchain-page__tx-hash"
        >
          {tx.tx_hash.slice(0, 8)}...
        </a>
      </div>
    </div>
  );
}

function NFTCard({ nft }: { nft: OnChainNFT }) {
  const chain = CHAIN_CONFIG[nft.chain] || { name: nft.chain, color: '#888', icon: '⬡' };

  return (
    <div className="onchain-page__nft-card">
      <div className="onchain-page__nft-image">
        {nft.thumbnail_url || nft.content_url ? (
          <img
            src={nft.thumbnail_url || nft.content_url || ''}
            alt={nft.name || 'NFT'}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="onchain-page__nft-placeholder">
            <Icons.Image size={32} />
          </div>
        )}
        <span
          className="onchain-page__nft-chain"
          style={{ backgroundColor: chain.color }}
        >
          {chain.name}
        </span>
      </div>
      <div className="onchain-page__nft-info">
        <p className="onchain-page__nft-name">{nft.name || `#${nft.token_id}`}</p>
        <p className="onchain-page__nft-collection">{nft.collection_name || 'Unknown Collection'}</p>
        {nft.estimated_value_usd && (
          <p className="onchain-page__nft-value">${fmt.currency(nft.estimated_value_usd, 2)}</p>
        )}
        {nft.rarity_rank && (
          <p className="onchain-page__nft-rarity">Rank #{nft.rarity_rank}</p>
        )}
      </div>
    </div>
  );
}

function ApprovalRow({ approval }: { approval: OnChainApproval }) {
  const chain = CHAIN_CONFIG[approval.chain] || { name: approval.chain, color: '#888', icon: '⬡' };
  const riskColors = RISK_COLORS[approval.risk_level] || RISK_COLORS.medium;

  return (
    <div
      className="onchain-page__approval-row"
      style={{
        background: riskColors.bg,
        borderLeft: `3px solid ${riskColors.border}`,
      }}
    >
      <div className="onchain-page__approval-token">
        {approval.token_logo ? (
          <img src={approval.token_logo} alt={approval.token_symbol} className="onchain-page__approval-logo" />
        ) : (
          <div className="onchain-page__approval-logo-fallback">{approval.token_symbol[0]}</div>
        )}
        <div>
          <p className="onchain-page__approval-symbol">{approval.token_symbol}</p>
          <span
            className="onchain-page__token-chain"
            style={{ backgroundColor: chain.color + '20', color: chain.color }}
          >
            {chain.name}
          </span>
        </div>
      </div>

      <div className="onchain-page__approval-spender">
        {approval.spender_protocol_logo ? (
          <img src={approval.spender_protocol_logo} alt="" className="onchain-page__approval-protocol-logo" />
        ) : null}
        <div>
          <p className="onchain-page__approval-protocol">
            {approval.spender_protocol_name || 'Unknown Protocol'}
          </p>
          <p className="onchain-page__approval-address">
            {approval.spender_address.slice(0, 8)}...{approval.spender_address.slice(-6)}
          </p>
        </div>
      </div>

      <div className="onchain-page__approval-amount">
        {approval.is_unlimited ? (
          <span className="onchain-page__approval-unlimited">∞ Illimité</span>
        ) : (
          <span>{fmt.number(approval.approved_amount, 4)}</span>
        )}
      </div>

      <div className="onchain-page__approval-risk">
        <span
          className="onchain-page__approval-risk-badge"
          style={{ backgroundColor: riskColors.bg, color: riskColors.text, border: `1px solid ${riskColors.border}` }}
        >
          {approval.risk_level === 'critical' ? '⚠️ Critique' :
           approval.risk_level === 'high' ? '🔴 Élevé' :
           approval.risk_level === 'medium' ? '🟠 Moyen' : '🟢 Faible'}
        </span>
        {approval.risk_reason && (
          <p className="onchain-page__approval-risk-reason">{approval.risk_reason}</p>
        )}
      </div>
    </div>
  );
}

function HistoryRow({ tx }: { tx: OnChainHistoryTx }) {
  const chain = CHAIN_CONFIG[tx.chain] || { name: tx.chain, color: '#888', icon: '⬡' };
  const cateInfo = HISTORY_CATE_LABELS[tx.cate_id || ''] || { label: tx.cate_id || 'Transaction', color: '#888' };

  // Calculate total amounts
  const totalSent = tx.sends.reduce((sum, s) => sum + (s.amount_usd || 0), 0);
  const totalReceived = tx.receives.reduce((sum, r) => sum + (r.amount_usd || 0), 0);

  return (
    <div className="onchain-page__history-row">
      <div className="onchain-page__history-type">
        <span
          className="onchain-page__history-badge"
          style={{ backgroundColor: cateInfo.color + '20', color: cateInfo.color }}
        >
          {cateInfo.label}
        </span>
        <span
          className="onchain-page__token-chain"
          style={{ backgroundColor: chain.color + '20', color: chain.color, marginLeft: '6px' }}
        >
          {chain.name}
        </span>
      </div>

      <div className="onchain-page__history-details">
        {tx.project_name && (
          <div className="onchain-page__history-protocol">
            {tx.project_logo && <img src={tx.project_logo} alt="" className="onchain-page__history-protocol-logo" />}
            <span>{tx.project_name}</span>
          </div>
        )}

        {tx.sends.length > 0 && (
          <div className="onchain-page__history-transfers">
            {tx.sends.slice(0, 2).map((s, i) => (
              <span key={i} className="onchain-page__history-send">
                -{fmt.number(s.amount, 4)} {s.symbol}
              </span>
            ))}
            {tx.sends.length > 2 && <span className="onchain-page__history-more">+{tx.sends.length - 2}</span>}
          </div>
        )}

        {tx.receives.length > 0 && (
          <div className="onchain-page__history-transfers">
            {tx.receives.slice(0, 2).map((r, i) => (
              <span key={i} className="onchain-page__history-receive">
                +{fmt.number(r.amount, 4)} {r.symbol}
              </span>
            ))}
            {tx.receives.length > 2 && <span className="onchain-page__history-more">+{tx.receives.length - 2}</span>}
          </div>
        )}
      </div>

      <div className="onchain-page__history-value">
        {totalSent > 0 && <p className="onchain-page__history-sent">-${fmt.currency(totalSent, 2)}</p>}
        {totalReceived > 0 && <p className="onchain-page__history-received">+${fmt.currency(totalReceived, 2)}</p>}
      </div>

      <div className="onchain-page__history-meta">
        <p className="onchain-page__history-date">
          {new Date(tx.block_timestamp).toLocaleDateString()}
        </p>
        <a
          href={`https://etherscan.io/tx/${tx.tx_hash}`}
          target="_blank"
          rel="noopener noreferrer"
          className="onchain-page__history-hash"
        >
          {tx.tx_hash.slice(0, 8)}...
        </a>
        {tx.gas_fee_usd && (
          <p className="onchain-page__history-gas">Gas: ${fmt.number(tx.gas_fee_usd, 2)}</p>
        )}
      </div>
    </div>
  );
}

function AddWalletModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (address: string, label: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!address.match(/^0x[a-fA-F0-9]{40}$/)) {
      setError(t('onchain.invalidAddress', 'Invalid Ethereum address'));
      return;
    }

    setIsLoading(true);
    try {
      const success = await onAdd(address, label);
      if (success) {
        setAddress('');
        setLabel('');
        onClose();
      } else {
        setError(t('onchain.addFailed', 'Failed to add wallet'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('onchain.addFailed', 'Failed to add wallet'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onchain-page__modal-overlay" onClick={onClose}>
      <div className="onchain-page__modal" onClick={(e) => e.stopPropagation()}>
        <div className="onchain-page__modal-header">
          <h3 className="onchain-page__modal-title">{t('onchain.addWallet', 'Add Wallet')}</h3>
          <button onClick={onClose} className="onchain-page__modal-close">
            <Icons.X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="onchain-page__form-group">
            <label className="onchain-page__form-label">{t('onchain.walletAddress', 'Wallet Address')}</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="0x..."
              className="onchain-page__form-input"
              autoFocus
            />
          </div>

          <div className="onchain-page__form-group">
            <label className="onchain-page__form-label">
              {t('onchain.label', 'Label')} <span style={{ color: '#666' }}>({t('common.optional', 'optional')})</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="My Main Wallet"
              className="onchain-page__form-input"
            />
          </div>

          {error && <p className="onchain-page__form-error">{error}</p>}

          <div className="onchain-page__form-actions">
            <button type="button" onClick={onClose} className="onchain-page__btn onchain-page__btn--secondary">
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading || !address}
              className="onchain-page__btn onchain-page__btn--primary"
            >
              {isLoading ? t('common.adding', 'Adding...') : t('common.add', 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================
// MAIN COMPONENT
// =====================================================

export function PortfolioOnChain({ userMode = 'simple' }: PortfolioOnChainProps) {
  const { t } = useTranslation();
  const { currencyInfo } = useCurrencyContext();
  const toast = useToast();

  const validTabs: TabType[] = ['overview', 'tokens', 'defi', 'nfts', 'approvals', 'history', 'transactions'];
  const getTabFromHash = (): TabType => {
    const hash = window.location.hash.replace('#', '') as TabType;
    return validTabs.includes(hash) ? hash : 'overview';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getTabFromHash);
  const [showAddWallet, setShowAddWallet] = useState(false);

  const {
    summary,
    wallets,
    tokens,
    defiPositions,
    transactions,
    nfts,
    approvals,
    history,
    isLoading,
    isSyncing,
    error,
    clearError,
    addWallet,
    deleteWallet,
    selectedWalletId,
    setSelectedWalletId,
    syncWallet,
    fetchTokens,
    fetchDefiPositions,
    fetchTransactions,
    fetchNFTs,
    fetchApprovals,
    fetchHistory,
    isLoggedIn,
  } = useOnChainPortfolio();

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };

  useEffect(() => {
    const handleHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (activeTab === 'tokens') fetchTokens(selectedWalletId || undefined);
    else if (activeTab === 'defi') fetchDefiPositions(selectedWalletId || undefined);
    else if (activeTab === 'nfts') fetchNFTs(selectedWalletId || undefined);
    else if (activeTab === 'approvals') fetchApprovals(selectedWalletId || undefined);
    else if (activeTab === 'history') fetchHistory(selectedWalletId || undefined);
    else if (activeTab === 'transactions') fetchTransactions(selectedWalletId || undefined);
  }, [activeTab, selectedWalletId, fetchTokens, fetchDefiPositions, fetchNFTs, fetchApprovals, fetchHistory, fetchTransactions]);

  const overviewStats = useMemo(() => {
    if (!summary) return null;
    return {
      totalValue: summary.totalValueUsd,
      change24h: summary.change24hUsd,
      change24hPercent: summary.change24hPercent,
      tokensCount: summary.tokensCount,
      chainsCount: summary.chainsCount,
    };
  }, [summary]);

  const handleAddWallet = async (address: string, label: string): Promise<boolean> => {
    const success = await addWallet(address, label);
    if (success) {
      toast(t('onchain.toasts.walletAdded', 'Wallet added successfully'), 'success');
      setTimeout(() => syncWallet(), 500);
    }
    return success;
  };

  const handleSync = async (walletId?: string) => {
    const success = await syncWallet(walletId);
    if (success) {
      toast(t('onchain.toasts.syncComplete', 'Sync completed'), 'success');
    } else {
      // Show specific error message if available
      const errorMsg = error?.message || t('onchain.toasts.syncFailed', 'Sync failed');
      toast(errorMsg, 'error');
    }
  };

  const handleDeleteWallet = async (walletId: string) => {
    const success = await deleteWallet(walletId);
    if (success) {
      toast(t('onchain.toasts.walletDeleted', 'Wallet supprimé'), 'success');
    } else {
      toast(t('onchain.toasts.deleteFailed', 'Échec de la suppression'), 'error');
    }
  };

  // Not logged in
  if (!isLoggedIn) {
    return (
      <div className="onchain-page">
        <div className="onchain-page__empty-card">
          <div className="onchain-page__empty-icon">
            <Icons.Lock size={36} />
          </div>
          <h2 className="onchain-page__empty-title">{t('onchain.loginRequired', 'Login Required')}</h2>
          <p className="onchain-page__empty-text">
            {t('onchain.loginMessage', 'Please log in to view your on-chain portfolio.')}
          </p>
        </div>
      </div>
    );
  }

  // Loading
  if (isLoading && wallets.length === 0) {
    return (
      <div className="onchain-page">
        <div className="onchain-page__loading">
          <div className="onchain-page__spinner" />
          <p>{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onchain-page">
      {/* Header */}
      <header className="onchain-page__header">
        <div>
          <h1 className="onchain-page__title">{t('onchain.title', 'Portfolio On-Chain')}</h1>
          <p className="onchain-page__subtitle">{t('onchain.subtitle', 'Real-time view of your blockchain assets')}</p>
        </div>

        <div className="onchain-page__header-actions">
          <button
            onClick={() => handleSync()}
            disabled={isSyncing}
            className="onchain-page__btn onchain-page__btn--primary"
          >
            <Icons.RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? t('onchain.syncing', 'Syncing...') : t('onchain.syncAll', 'Sync All')}
          </button>
          <button
            onClick={() => setShowAddWallet(true)}
            className="onchain-page__btn onchain-page__btn--secondary"
          >
            <Icons.Plus size={16} />
            {t('onchain.addWallet', 'Add Wallet')}
          </button>
        </div>
      </header>

      {/* Wallets Grid */}
      {wallets.length > 0 && (
        <section className="onchain-page__wallets-section">
          <div className="onchain-page__wallets-grid">
            {wallets.map((wallet) => (
              <WalletCard
                key={wallet.id}
                wallet={wallet}
                isSelected={wallet.id === selectedWalletId}
                onClick={() => setSelectedWalletId(wallet.id)}
                onSync={() => handleSync(wallet.id)}
                onDelete={() => handleDeleteWallet(wallet.id)}
                isSyncing={isSyncing}
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {wallets.length === 0 && (
        <div className="onchain-page__empty-card">
          <div className="onchain-page__empty-icon">
            <Icons.Wallet size={36} />
          </div>
          <h2 className="onchain-page__empty-title">{t('onchain.noWallets', 'No Wallets Added')}</h2>
          <p className="onchain-page__empty-text">
            {t('onchain.noWalletsMessage', 'Add your first Ethereum wallet to start tracking your on-chain portfolio.')}
          </p>
          <button
            onClick={() => setShowAddWallet(true)}
            className="onchain-page__btn onchain-page__btn--primary"
          >
            <Icons.Plus size={18} />
            {t('onchain.addWallet', 'Add Wallet')}
          </button>
        </div>
      )}

      {/* KPI Row */}
      {overviewStats && wallets.length > 0 && (
        <div className="onchain-page__kpi-row">
          {/* Total Value */}
          <div
            className="onchain-page__kpi-card"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(99, 102, 241, 0.08) 100%)',
              borderColor: 'rgba(139, 92, 246, 0.25)',
            }}
          >
            <div className="onchain-page__kpi-watermark">
              <Icons.Wallet size={56} />
            </div>
            <div className="onchain-page__kpi-content">
              <span className="onchain-page__kpi-label">{t('onchain.totalValue', 'Total Value')}</span>
              <span className="onchain-page__kpi-value" style={{ color: '#a78bfa' }}>
                {currencyInfo.symbol}{fmt.currency(overviewStats.totalValue, 0)}
              </span>
            </div>
          </div>

          {/* 24h Change */}
          <div
            className="onchain-page__kpi-card"
            style={{
              background: overviewStats.change24h >= 0
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(0, 212, 170, 0.08) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(255, 107, 107, 0.08) 100%)',
              borderColor: overviewStats.change24h >= 0 ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
            }}
          >
            <div className="onchain-page__kpi-watermark">
              <Icons.TrendingUp size={56} />
            </div>
            <div className="onchain-page__kpi-content">
              <span className="onchain-page__kpi-label">{t('onchain.24hChange', '24h Change')}</span>
              <span
                className="onchain-page__kpi-value"
                style={{ color: overviewStats.change24h >= 0 ? '#10b981' : '#ef4444' }}
              >
                {overviewStats.change24h >= 0 ? '+' : ''}{currencyInfo.symbol}{fmt.currency(overviewStats.change24h, 0)}
              </span>
              <div className="onchain-page__kpi-sub">
                <span>{fmt.number(overviewStats.change24hPercent, 2)}%</span>
              </div>
            </div>
          </div>

          {/* Tokens */}
          <div
            className="onchain-page__kpi-card"
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)',
              borderColor: 'rgba(6, 182, 212, 0.25)',
            }}
          >
            <div className="onchain-page__kpi-watermark">
              <Icons.Coins size={56} />
            </div>
            <div className="onchain-page__kpi-content">
              <span className="onchain-page__kpi-label">{t('onchain.tokens', 'Tokens')}</span>
              <span className="onchain-page__kpi-value" style={{ color: '#22d3ee' }}>
                {overviewStats.tokensCount}
              </span>
            </div>
          </div>

          {/* Chains */}
          <div
            className="onchain-page__kpi-card"
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(234, 179, 8, 0.08) 100%)',
              borderColor: 'rgba(245, 158, 11, 0.25)',
            }}
          >
            <div className="onchain-page__kpi-watermark">
              <Icons.Link size={56} />
            </div>
            <div className="onchain-page__kpi-content">
              <span className="onchain-page__kpi-label">{t('onchain.chains', 'Chains')}</span>
              <span className="onchain-page__kpi-value" style={{ color: '#fbbf24' }}>
                {overviewStats.chainsCount}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {wallets.length > 0 && (
        <>
          <div className="onchain-page__tabs">
            {validTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`onchain-page__tab ${activeTab === tab ? 'onchain-page__tab--active' : ''}`}
              >
                {t(`onchain.tabs.${tab}`, tab.charAt(0).toUpperCase() + tab.slice(1))}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="onchain-page__content">
            {/* Overview */}
            {activeTab === 'overview' && (
              <div className="onchain-page__token-list">
                {/* V2.F2: h3 → h2 to satisfy axe `heading-order` (page has only h1 above; no h2 was bridging). Same CSS class, same visual. */}
                <h2 className="onchain-page__section-title">{t('onchain.topHoldings', 'Top Holdings')}</h2>
                {tokens.slice(0, 5).map((token) => (
                  <TokenRow key={`${token.chain}-${token.token_address || 'native'}`} token={token} />
                ))}
                {tokens.length > 5 && (
                  <button onClick={() => handleTabChange('tokens')} className="onchain-page__view-all">
                    {t('onchain.viewAllTokens', 'View all {{count}} tokens', { count: tokens.length })}
                  </button>
                )}
                {tokens.length === 0 && (
                  <div className="onchain-page__empty-state">
                    <Icons.Inbox size={48} />
                    <p>{t('onchain.noTokens', 'No tokens found')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Tokens */}
            {activeTab === 'tokens' && (
              <div className="onchain-page__token-list">
                {tokens.length === 0 ? (
                  <div className="onchain-page__empty-state">
                    <Icons.Inbox size={48} />
                    <p>{t('onchain.noTokens', 'No tokens found')}</p>
                  </div>
                ) : (
                  tokens.map((token) => (
                    <TokenRow key={`${token.chain}-${token.token_address || 'native'}`} token={token} />
                  ))
                )}
              </div>
            )}

            {/* DeFi */}
            {activeTab === 'defi' && (
              <div className="onchain-page__defi-grid">
                {defiPositions.length === 0 ? (
                  <div className="onchain-page__empty-state" style={{ gridColumn: '1 / -1' }}>
                    <Icons.Inbox size={48} />
                    <p>{t('onchain.noDefi', 'No DeFi positions found')}</p>
                  </div>
                ) : (
                  defiPositions.map((position) => (
                    <DefiPositionCard key={position.id} position={position} />
                  ))
                )}
              </div>
            )}

            {/* NFTs */}
            {activeTab === 'nfts' && (
              <div className="onchain-page__nft-grid">
                {nfts.length === 0 ? (
                  <div className="onchain-page__empty-state" style={{ gridColumn: '1 / -1' }}>
                    <Icons.Image size={48} />
                    <p>{t('onchain.noNFTs', 'Aucun NFT trouvé')}</p>
                  </div>
                ) : (
                  nfts.map((nft) => (
                    <NFTCard key={`${nft.chain}-${nft.contract_address}-${nft.token_id}`} nft={nft} />
                  ))
                )}
              </div>
            )}

            {/* Approvals */}
            {activeTab === 'approvals' && (
              <div className="onchain-page__approvals-list">
                {approvals.length === 0 ? (
                  <div className="onchain-page__empty-state">
                    <Icons.Shield size={48} />
                    <p>{t('onchain.noApprovals', 'Aucune approbation de token trouvée')}</p>
                  </div>
                ) : (
                  <>
                    <div className="onchain-page__approvals-summary">
                      <div className="onchain-page__approvals-stat">
                        <span className="onchain-page__approvals-stat-value">{approvals.length}</span>
                        <span className="onchain-page__approvals-stat-label">{t('onchain.totalApprovals', 'Approbations')}</span>
                      </div>
                      <div className="onchain-page__approvals-stat onchain-page__approvals-stat--critical">
                        <span className="onchain-page__approvals-stat-value">
                          {approvals.filter(a => a.risk_level === 'critical' || a.risk_level === 'high').length}
                        </span>
                        <span className="onchain-page__approvals-stat-label">{t('onchain.highRiskApprovals', 'À risque')}</span>
                      </div>
                      <div className="onchain-page__approvals-stat onchain-page__approvals-stat--unlimited">
                        <span className="onchain-page__approvals-stat-value">
                          {approvals.filter(a => a.is_unlimited).length}
                        </span>
                        <span className="onchain-page__approvals-stat-label">{t('onchain.unlimitedApprovals', 'Illimités')}</span>
                      </div>
                    </div>
                    {approvals.map((approval) => (
                      <ApprovalRow key={`${approval.chain}-${approval.token_address}-${approval.spender_address}`} approval={approval} />
                    ))}
                  </>
                )}
              </div>
            )}

            {/* History (DeBank format) */}
            {activeTab === 'history' && (
              <div className="onchain-page__history-list">
                {!history || history.data.length === 0 ? (
                  <div className="onchain-page__empty-state">
                    <Icons.Clock size={48} />
                    <p>{t('onchain.noHistory', 'Aucun historique trouvé')}</p>
                  </div>
                ) : (
                  <>
                    {history.data.map((tx) => (
                      <HistoryRow key={`${tx.chain}-${tx.tx_hash}`} tx={tx} />
                    ))}
                    {history.totalPages > 1 && (
                      <div className="onchain-page__pagination">
                        <button
                          disabled={history.page <= 1}
                          onClick={() => fetchHistory(selectedWalletId || undefined, undefined, { page: history.page - 1, limit: 20 })}
                          className="onchain-page__pagination-btn"
                        >
                          {t('common.previous', 'Précédent')}
                        </button>
                        <span className="onchain-page__pagination-info">
                          {history.page} / {history.totalPages}
                        </span>
                        <button
                          disabled={history.page >= history.totalPages}
                          onClick={() => fetchHistory(selectedWalletId || undefined, undefined, { page: history.page + 1, limit: 20 })}
                          className="onchain-page__pagination-btn"
                        >
                          {t('common.next', 'Suivant')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Transactions */}
            {activeTab === 'transactions' && (
              <div className="onchain-page__tx-list">
                {transactions?.data.length === 0 ? (
                  <div className="onchain-page__empty-state">
                    <Icons.Inbox size={48} />
                    <p>{t('onchain.noTransactions', 'No transactions found')}</p>
                  </div>
                ) : (
                  <>
                    {transactions?.data.map((tx) => (
                      <TransactionRow key={tx.tx_hash} tx={tx} />
                    ))}
                    {transactions && transactions.totalPages > 1 && (
                      <div className="onchain-page__pagination">
                        <button
                          disabled={transactions.page <= 1}
                          onClick={() => fetchTransactions(selectedWalletId || undefined, undefined, { page: transactions.page - 1, limit: 20 })}
                          className="onchain-page__pagination-btn"
                        >
                          {t('common.previous', 'Previous')}
                        </button>
                        <span className="onchain-page__pagination-info">
                          {transactions.page} / {transactions.totalPages}
                        </span>
                        <button
                          disabled={transactions.page >= transactions.totalPages}
                          onClick={() => fetchTransactions(selectedWalletId || undefined, undefined, { page: transactions.page + 1, limit: 20 })}
                          className="onchain-page__pagination-btn"
                        >
                          {t('common.next', 'Next')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Add Wallet Modal */}
      <AddWalletModal
        isOpen={showAddWallet}
        onClose={() => setShowAddWallet(false)}
        onAdd={handleAddWallet}
      />

      {/* Error Banner - Shows DeBank API issues */}
      {error && (
        <div className={`onchain-page__error-banner ${error.message?.includes('DeBank') || error.message?.includes('crédit') ? 'onchain-page__error-banner--warning' : ''}`}>
          <div className="onchain-page__error-icon">
            <Icons.AlertTriangle size={24} />
          </div>
          <div className="onchain-page__error-content">
            <p className="onchain-page__error-title">
              {error.message?.includes('crédit') || error.message?.includes('Credits')
                ? t('onchain.errors.apiCredits', 'Crédits API insuffisants')
                : error.message?.includes('DeBank')
                ? t('onchain.errors.apiError', 'Erreur API DeBank')
                : t('onchain.errors.syncError', 'Erreur de synchronisation')}
            </p>
            <p className="onchain-page__error-message">{error.message}</p>
          </div>
          <button
            className="onchain-page__error-close"
            onClick={clearError}
          >
            <Icons.X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

export default PortfolioOnChain;
